import { useState, useEffect } from "react";
import { resolveBackendUrl } from "./backendUrl";

const HW_CORES = navigator.hardwareConcurrency || 4;
const DEVICE_MEM_GB = navigator.deviceMemory || null;

/**
 * Real browser + hardware stats we CAN measure:
 *  - CPU cores (static), device memory GB, JS heap, FPS, main thread lag %,
 *    backend latency, battery, network type, online status.
 *
 * AŞAMA 13 (Düzeltme 1 · performans): Ölçüm artık TEK KAYNAKTAN yapılır.
 * Önceden her `useSystemStats()` tüketicisi kendi döngülerini kuruyordu ve
 * ana iş parçacığı gecikmesi 100 ms'de bir `setState` tetikliyordu
 * (saniyede ~10 render) + 4 saniyede bir backend ping (dakikada 30 istek).
 * Şimdi:
 *  · Ölçüm sık yapılır, ekrana yansıma SANİYEDE 1 (biriktirilip yumuşatılır).
 *  · Backend ping 60 saniyede 1.
 *  · `document.hidden` iken ölçüm ve ping TAMAMEN DURUR.
 *  · Tüketici yoksa hiçbir döngü çalışmaz.
 */

const PUBLISH_MS = 1000;   // ekrana yansıma
const LAG_MS = 100;        // gecikme ölçüm adımı (render tetiklemez)
const PING_MS = 60000;     // backend ping
const SMOOTH_N = 5;        // son N ölçümün ortalaması

let state = {
  cores: HW_CORES,
  deviceMemGB: DEVICE_MEM_GB,
  ramPct: null,
  ramUsedMB: null,
  ramLimitMB: null,
  fps: 60,
  loadPct: 0,
  latency: null,
  online: navigator.onLine,
  network: navigator.connection?.effectiveType || null,
  battery: null,
  charging: null,
  platform: navigator.platform || "",
  userAgent: navigator.userAgentData?.platform || navigator.platform || "",
};

const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn(state));
const patch = (p) => {
  state = { ...state, ...p };
  notify();
};

const ortalama = (arr, varsayilan) =>
  arr.length ? Math.round(arr.reduce((a, b) => a + b, 0) / arr.length) : varsayilan;

let raf = null;
let publishIv = null;
let pingIv = null;
let sensorsBound = false;
let battery = null;
let batteryUpdate = null;

// Biriktiriciler
let fpsAcc = 0;
let fpsSamples = 0;
let lastFrame = 0;
let lastLag = 0;
let lagAcc = [];
let fpsGecmis = [];
let loadGecmis = [];

const frame = (now) => {
  const dt = now - lastFrame;
  lastFrame = now;
  if (dt > 0 && dt < 500) {
    fpsAcc += 1000 / dt;
    fpsSamples++;
    // Ana iş parçacığı gecikmesi: kare aralığından türetilir, ayrı zamanlayıcı
    // ve ayrı setState gerekmez.
    if (now - lastLag >= LAG_MS) {
      lastLag = now;
      lagAcc.push(Math.min(100, Math.round((Math.max(0, dt - 16.7) / 60) * 100)));
      if (lagAcc.length > 20) lagAcc.shift();
    }
  }
  raf = requestAnimationFrame(frame);
};

const publish = () => {
  const avgFps = fpsSamples > 0 ? Math.min(240, Math.round(fpsAcc / fpsSamples)) : 60;
  fpsAcc = 0;
  fpsSamples = 0;
  fpsGecmis.push(avgFps);
  if (fpsGecmis.length > SMOOTH_N) fpsGecmis.shift();
  loadGecmis.push(ortalama(lagAcc, 0));
  if (loadGecmis.length > SMOOTH_N) loadGecmis.shift();
  lagAcc = [];

  const p = { fps: ortalama(fpsGecmis, 60), loadPct: ortalama(loadGecmis, 0) };
  if (performance.memory) {
    const used = performance.memory.usedJSHeapSize;
    const limit = performance.memory.jsHeapSizeLimit;
    p.ramPct = Math.round((used / limit) * 100);
    p.ramUsedMB = Math.round(used / 1024 / 1024);
    p.ramLimitMB = Math.round(limit / 1024 / 1024);
  }
  patch(p);
};

const ping = async () => {
  if (document.hidden) return;
  const t0 = performance.now();
  try {
    const r = await fetch(`${resolveBackendUrl()}/api/`, { cache: "no-store" });
    await r.text();
    patch({ latency: Math.round(performance.now() - t0), online: true });
  } catch {
    patch({ latency: null, online: false });
  }
};

const startLoops = () => {
  if (raf != null || document.hidden) return;
  lastFrame = performance.now();
  lastLag = lastFrame;
  raf = requestAnimationFrame(frame);
  publishIv = setInterval(publish, PUBLISH_MS);
  pingIv = setInterval(ping, PING_MS);
  ping();
};

const stopLoops = () => {
  if (raf != null) cancelAnimationFrame(raf);
  raf = null;
  clearInterval(publishIv);
  clearInterval(pingIv);
  publishIv = null;
  pingIv = null;
};

const onVisibility = () => {
  if (!listeners.size) return;
  if (document.hidden) stopLoops();
  else startLoops();
};

const bindSensors = () => {
  if (sensorsBound) return;
  sensorsBound = true;
  window.addEventListener("online", () => patch({ online: true }));
  window.addEventListener("offline", () => patch({ online: false }));
  document.addEventListener("visibilitychange", onVisibility);
  if (navigator.connection) {
    const u = () => patch({ network: navigator.connection.effectiveType });
    navigator.connection.addEventListener("change", u);
  }
  if (navigator.getBattery) {
    navigator
      .getBattery()
      .then((b) => {
        battery = b;
        batteryUpdate = () =>
          patch({ battery: Math.round(b.level * 100), charging: b.charging });
        batteryUpdate();
        b.addEventListener("levelchange", batteryUpdate);
        b.addEventListener("chargingchange", batteryUpdate);
      })
      .catch(() => {});
  }
};

export const useSystemStats = () => {
  const [snapshot, setSnapshot] = useState(state);
  useEffect(() => {
    bindSensors();
    listeners.add(setSnapshot);
    if (listeners.size === 1) startLoops();
    return () => {
      listeners.delete(setSnapshot);
      // Tüketici kalmadıysa ölçüm tamamen dursun.
      if (!listeners.size) stopLoops();
    };
  }, []);
  return snapshot;
};

// Helper: FPS → percentage (60 fps = 100%)
export const fpsToPct = (fps) => Math.min(100, Math.round((fps / 60) * 100));

// Helper: prettify platform
export const prettyPlatform = (p) => {
  const s = (p || "").toLowerCase();
  if (s.includes("win")) return "Windows";
  if (s.includes("mac")) return "macOS";
  if (s.includes("linux")) return "Linux";
  if (s.includes("android")) return "Android";
  if (s.includes("iphone") || s.includes("ipad") || s.includes("ios")) return "iOS";
  return p || "?";
};
